import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowDown, ArrowUp, Store, X } from "lucide-react";
import type { ProductKind, StoreProduct } from "@baze/api-client";
import type { SectionNode } from "@baze/schema";
import { Badge } from "@/components/ui/badge";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/select";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { SwitchRow } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { RupiahInput } from "@/components/RupiahInput";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { qk } from "@/lib/query";
import { t } from "@/i18n/id";

const s = t.editor.store;
type Set = (path: string, value: unknown) => void;
type Products = Extract<SectionNode, { type: "products" }>;
type Feature = Extract<SectionNode, { type: "product-feature" }>;

function useStoreProducts(siteId: string) {
  return useQuery({ queryKey: qk.store.products(siteId), queryFn: () => api.store.products(siteId), staleTime: 30_000 });
}

/** Products section: the owner's own items, or the products of the Toko (with checkout). */
export function ProductsSource({ siteId, section, set }: { siteId: string; section: Products; set: Set }) {
  const src = section.content.dataSource;
  const store = src?.type === "store";
  return (
    <div className="space-y-3 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-900/5">
      <Segmented
        size="sm"
        full
        label={s.source}
        value={store ? "store" : "static"}
        onChange={(v) => set("dataSource", v === "store" ? { type: "store" } : undefined)}
        options={[
          { value: "static", label: s.sourceStatic },
          { value: "store", label: s.sourceStore },
        ]}
      />
      {store ? <StoreSource siteId={siteId} source={src} set={set} /> : <p className="text-[12px] leading-snug text-slate-500">{s.staticHint}</p>}
    </div>
  );
}

function StoreSource({ siteId, source, set }: { siteId: string; source: { type: "store"; kinds?: ProductKind[]; slugs?: string[]; limit?: number }; set: Set }) {
  const list = useStoreProducts(siteId);
  if (list.isPending) return <Skeleton className="h-24 rounded-xl" />;
  if (list.isError) return null;
  const items = list.data.items;
  if (!list.data.enabled || items.length === 0) return <StoreEmpty siteId={siteId} enabled={list.data.enabled} />;
  const picked = source.slugs ?? [];
  const mode = picked.length ? "pick" : "all";
  const bySlug = new Map(items.map((p) => [p.slug, p]));
  const update = (patch: Partial<typeof source>) => {
    const next = { ...source, ...patch };
    for (const k of Object.keys(next) as Array<keyof typeof next>) if (next[k] === undefined || (Array.isArray(next[k]) && !(next[k] as unknown[]).length)) delete next[k];
    set("dataSource", next);
  };
  return (
    <div className="space-y-3">
      <Segmented
        size="sm"
        full
        label={s.which}
        value={mode}
        onChange={(v) => update(v === "all" ? { slugs: undefined } : { slugs: items.filter((p) => p.status === "active").slice(0, 6).map((p) => p.slug), kinds: undefined })}
        options={[
          { value: "all", label: s.all },
          { value: "pick", label: s.pick },
        ]}
      />
      {mode === "all" ? (
        <>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={s.kinds}>
            {(["ebook", "course", "asset", "audio", "other"] as ProductKind[]).map((k) => {
              const on = !source.kinds?.length || source.kinds.includes(k);
              return (
                <button
                  key={k}
                  type="button"
                  aria-pressed={on}
                  onClick={() => {
                    const all: ProductKind[] = ["ebook", "course", "asset", "audio", "other"];
                    const cur = source.kinds?.length ? source.kinds : all;
                    const next = on ? cur.filter((x) => x !== k) : [...cur, k];
                    update({ kinds: next.length === all.length || !next.length ? undefined : next });
                  }}
                  className="h-8 rounded-pill bg-white px-3 text-[12px] font-semibold text-slate-600 ring-1 ring-line aria-pressed:bg-primary-soft aria-pressed:text-brand-800 aria-pressed:ring-primary/40"
                >
                  {t.store.kinds[k]}
                </button>
              );
            })}
          </div>
          <Field label={s.limit}>
            <NativeSelect selectSize="sm" value={String(source.limit ?? "")} onChange={(e) => update({ limit: e.target.value ? Number(e.target.value) : undefined })}>
              <option value="">{s.limitAll}</option>
              {[3, 4, 6, 8, 12].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </NativeSelect>
          </Field>
        </>
      ) : (
        <div className="space-y-2">
          <ul className="space-y-1.5">
            {picked.map((slug, i) => {
              const p = bySlug.get(slug);
              return (
                <li key={slug} className="flex items-center gap-2 rounded-xl bg-white p-2 ring-1 ring-line">
                  <Thumb product={p} />
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-800">{p?.title ?? s.missing}</span>
                  {p && p.status !== "active" ? <Badge tone="warning">{t.store.products.status[p.status]}</Badge> : null}
                  <IconBtn label={s.up} disabled={i === 0} onClick={() => update({ slugs: swap(picked, i, i - 1) })}>
                    <ArrowUp className="size-3.5" aria-hidden="true" />
                  </IconBtn>
                  <IconBtn label={s.down} disabled={i === picked.length - 1} onClick={() => update({ slugs: swap(picked, i, i + 1) })}>
                    <ArrowDown className="size-3.5" aria-hidden="true" />
                  </IconBtn>
                  <IconBtn label={s.remove} onClick={() => update({ slugs: picked.filter((x) => x !== slug) })}>
                    <X className="size-3.5" aria-hidden="true" />
                  </IconBtn>
                </li>
              );
            })}
          </ul>
          {picked.length < 12 ? (
            <NativeSelect selectSize="sm" value="" aria-label={s.add} onChange={(e) => e.target.value && update({ slugs: [...picked, e.target.value] })}>
              <option value="">{s.add}</option>
              {items
                .filter((p) => !picked.includes(p.slug))
                .map((p) => (
                  <option key={p.slug} value={p.slug}>
                    {p.title}
                    {p.status !== "active" ? ` (${t.store.products.status[p.status]})` : ""}
                  </option>
                ))}
            </NativeSelect>
          ) : null}
        </div>
      )}
      {items.some((p) => p.status !== "active") ? <p className="text-[12px] text-slate-500">{s.draftsHidden}</p> : null}
      <ManageLink siteId={siteId} />
    </div>
  );
}

/** Produk Unggulan: the headline and pitch are the section's; price, cover and files come from the Toko. */
export function ProductFeatureForm({ siteId, section, set }: { siteId: string; section: Feature; set: Set }) {
  const c = section.content;
  const list = useStoreProducts(siteId);
  const items = list.data?.items ?? [];
  const linked = c.productSlug ? items.find((p) => p.slug === c.productSlug) : items[0];
  return (
    <div className="space-y-3">
      <div className="space-y-2 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-900/5">
        <p className="text-[12px] font-medium text-slate-500">{s.product}</p>
        {list.isPending ? (
          <Skeleton className="h-12 rounded-xl" />
        ) : items.length ? (
          <>
            <NativeSelect selectSize="sm" value={c.productSlug ?? ""} onChange={(e) => set("productSlug", e.target.value || undefined)}>
              <option value="">{s.firstProduct}</option>
              {items.map((p) => (
                <option key={p.slug} value={p.slug}>
                  {p.title}
                  {p.status !== "active" ? ` (${t.store.products.status[p.status]})` : ""}
                </option>
              ))}
            </NativeSelect>
            {linked ? (
              <div className="flex items-center gap-2 rounded-xl bg-white p-2 ring-1 ring-line">
                <Thumb product={linked} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-slate-800">{linked.title}</p>
                  <p className="text-[12px] text-slate-500">{linked.status === "active" ? s.onSale : s.notOnSale}</p>
                </div>
              </div>
            ) : null}
            <ManageLink siteId={siteId} />
          </>
        ) : (
          <StoreEmpty siteId={siteId} enabled={list.data?.enabled ?? false} />
        )}
      </div>
      <Field label={t.editor.fields.eyebrow} optional>
        <Input value={c.eyebrow ?? ""} maxLength={60} onChange={(e) => set("eyebrow", e.target.value || undefined)} />
      </Field>
      <Field label={s.headline} hint={s.headlineHint}>
        <Input value={c.title} maxLength={120} onChange={(e) => set("title", e.target.value)} />
      </Field>
      <Field label={t.editor.fields.description} optional>
        <Textarea rows={3} value={c.description ?? ""} maxLength={400} onChange={(e) => set("description", e.target.value || undefined)} />
      </Field>
      <Field label={s.bullets} hint={s.bulletsHint} optional>
        <Textarea
          rows={4}
          value={(c.bullets ?? []).join("\n")}
          onChange={(e) => {
            const lines = e.target.value.split("\n").map((l) => l.slice(0, 140));
            set("bullets", lines.some((l) => l.trim()) ? lines.slice(0, 6) : undefined);
          }}
        />
      </Field>
      <Field label={s.guarantee} optional>
        <Input value={c.guarantee ?? ""} maxLength={160} placeholder={s.guaranteePlaceholder} onChange={(e) => set("guarantee", e.target.value || undefined)} />
      </Field>
      <Field label={s.ctaLabel} optional>
        <Input value={c.ctaLabel ?? ""} maxLength={30} placeholder={s.ctaPlaceholder} onChange={(e) => set("ctaLabel", e.target.value || undefined)} />
      </Field>
      <SwitchRow id={`tiers-${section.id}`} label={s.showTiers} hint={s.showTiersHint} checked={!!c.showTiers} onCheckedChange={(v) => set("showTiers", v || undefined)} />
      <details className="rounded-lg bg-white ring-1 ring-slate-900/8">
        <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-slate-700">{s.fallback}</summary>
        <div className="space-y-3 px-3 pb-3">
          <p className="text-[12px] text-slate-500">{s.fallbackHint}</p>
          <Field label={t.editor.fields.price} optional>
            <RupiahInput value={c.price} onChange={(v) => set("price", v)} />
          </Field>
        </div>
      </details>
    </div>
  );
}

function StoreEmpty({ siteId, enabled }: { siteId: string; enabled: boolean }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-white p-3 ring-1 ring-line">
      <Store className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
      <div className="text-[13px] leading-snug">
        <p className="font-semibold text-slate-800">{enabled ? s.emptyTitle : s.lockedTitle}</p>
        <p className="mt-0.5 text-slate-500">{enabled ? s.emptyBody : s.lockedBody}</p>
        <Link to={`/situs/${siteId}/toko${enabled ? "/produk" : ""}`} className="mt-1.5 inline-flex items-center gap-1 font-semibold text-primary hover:underline">
          {enabled ? s.addProduct : s.openStore}
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function ManageLink({ siteId }: { siteId: string }) {
  return (
    <Link to={`/situs/${siteId}/toko/produk`} className="inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline">
      {s.manage}
      <ArrowRight className="size-3.5" aria-hidden="true" />
    </Link>
  );
}

function Thumb({ product }: { product?: StoreProduct }) {
  return <span className={cn("block size-9 shrink-0 overflow-hidden rounded-lg bg-slate-100", !product?.cover && "ring-1 ring-slate-900/5")}>{product?.cover ? <img src={product.cover} alt="" className="h-full w-full object-cover" /> : null}</span>;
}

function IconBtn({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-label={label} disabled={disabled} onClick={onClick} className="grid size-7 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30">
      {children}
    </button>
  );
}

const swap = (list: string[], a: number, b: number) => {
  const next = [...list];
  [next[a], next[b]] = [next[b]!, next[a]!];
  return next;
};
